/**
 * <hero-carousel>
 *
 * Autoplay via a single setTimeout per slide. Progress UI is dots only (no fill animation).
 * Manual navigation: dots, prev/next arrows and horizontal swipe (always wraps around).
 */
class HeroCarousel extends HTMLElement {
  static SLIDE_DURATION_MS = 3000;
  static SWIPE_MIN_PX = 40;
  /** A click this soon after a swipe is the end of the gesture, not a tap on the slide link. */
  static SWIPE_CLICK_GUARD_MS = 400;

  constructor() {
    super();
    this.currentIndex = 0;
    this.timeoutId = null;
    this.slides = [];
    this.segments = [];
    this.isPaused = false;
    this.isInViewport = true;
    this.reducedMotion = false;
    this.io = null;
    this.arrows = [];
    this.track = null;
    this.swipeStart = null;
    this.swipedAt = 0;

    this._onArrowClick = this._onArrowClick.bind(this);
    this._onSwipeStart = this._onSwipeStart.bind(this);
    this._onSwipeEnd = this._onSwipeEnd.bind(this);
    this._onSwipeCancel = this._onSwipeCancel.bind(this);
    this._onTrackClick = this._onTrackClick.bind(this);
    this._onSegmentClick = this._onSegmentClick.bind(this);
    this._onSegmentKeydown = this._onSegmentKeydown.bind(this);
    this._onPointerEnter = this._onPointerEnter.bind(this);
    this._onPointerLeave = this._onPointerLeave.bind(this);
    this._onFocusIn = this._onFocusIn.bind(this);
    this._onFocusOut = this._onFocusOut.bind(this);
    this._onVisibilityChange = this._onVisibilityChange.bind(this);
    this._onIntersect = this._onIntersect.bind(this);
  }

  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.hero-carousel__slide'));
    this.segments = Array.from(this.querySelectorAll('.hero-carousel__segment'));

    if (this.slides.length === 0) return;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.autoplay = this.dataset.autoplay !== 'false' && !this.reducedMotion;
    this.loop = this.dataset.loop !== 'false';
    this.pauseOnHover = this.dataset.pauseOnHover !== 'false';

    this._activate(0, { restartTimer: false });

    if (this.segments.length) {
      this.segments.forEach((seg) => {
        seg.addEventListener('click', this._onSegmentClick);
        seg.addEventListener('keydown', this._onSegmentKeydown);
      });
    }

    if (this.slides.length > 1) {
      this.arrows = Array.from(this.querySelectorAll('.hero-carousel__arrow'));
      this.arrows.forEach((arrow) => arrow.addEventListener('click', this._onArrowClick));

      this.track = this.querySelector('.hero-carousel__slides');
      if (this.track) {
        this.track.addEventListener('pointerdown', this._onSwipeStart);
        this.track.addEventListener('click', this._onTrackClick, true);
      }
    }

    if (this.pauseOnHover) {
      this.addEventListener('pointerenter', this._onPointerEnter);
      this.addEventListener('pointerleave', this._onPointerLeave);
      this.addEventListener('focusin', this._onFocusIn);
      this.addEventListener('focusout', this._onFocusOut);
    }

    document.addEventListener('visibilitychange', this._onVisibilityChange);

    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver(this._onIntersect, { threshold: 0.1 });
      this.io.observe(this);
    }

    if (this.autoplay && this.slides.length > 1) {
      this._startAutoplay();
    }
  }

  disconnectedCallback() {
    this._clearTimer();
    if (this.segments.length) {
      this.segments.forEach((seg) => {
        seg.removeEventListener('click', this._onSegmentClick);
        seg.removeEventListener('keydown', this._onSegmentKeydown);
      });
    }
    this.arrows.forEach((arrow) => arrow.removeEventListener('click', this._onArrowClick));
    if (this.track) {
      this.track.removeEventListener('pointerdown', this._onSwipeStart);
      this.track.removeEventListener('click', this._onTrackClick, true);
    }
    this._onSwipeCancel();
    this.removeEventListener('pointerenter', this._onPointerEnter);
    this.removeEventListener('pointerleave', this._onPointerLeave);
    this.removeEventListener('focusin', this._onFocusIn);
    this.removeEventListener('focusout', this._onFocusOut);
    document.removeEventListener('visibilitychange', this._onVisibilityChange);
    if (this.io) this.io.disconnect();
  }

  _slideDurationMs() {
    const slide = this.slides[this.currentIndex];
    const seconds = parseFloat(slide?.dataset.duration);
    if (Number.isFinite(seconds) && seconds > 0) {
      return seconds * 1000;
    }
    return HeroCarousel.SLIDE_DURATION_MS;
  }

  /**
   * @param {number} index
   * @param {{restartTimer?: boolean}} [opts]
   */
  _activate(index, { restartTimer = true } = {}) {
    if (index < 0 || index >= this.slides.length) return;

    this.currentIndex = index;

    this.slides.forEach((slide, i) => {
      if (i === index) {
        slide.setAttribute('data-active', '');
      } else {
        slide.removeAttribute('data-active');
      }
    });

    this._updateSegments(index);

    this.dispatchEvent(
      new CustomEvent('hero-carousel:change', {
        bubbles: true,
        detail: { index, total: this.slides.length },
      })
    );

    if (restartTimer && this.autoplay && this.slides.length > 1) {
      this._startAutoplay();
    }
  }

  _updateSegments(activeIndex) {
    if (!this.segments.length) return;

    this.segments.forEach((seg, i) => {
      if (i === activeIndex) {
        seg.setAttribute('aria-current', 'true');
        seg.setAttribute('data-state', 'active');
      } else {
        seg.removeAttribute('aria-current');
        seg.removeAttribute('data-state');
      }
    });
  }

  _startAutoplay() {
    this._clearTimer();
    if (!this.autoplay || this.slides.length <= 1 || this.isPaused || !this.isInViewport) return;

    this.timeoutId = window.setTimeout(() => {
      this.timeoutId = null;
      this._next();
    }, this._slideDurationMs());
  }

  _clearTimer() {
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  _next() {
    let next = this.currentIndex + 1;
    if (next >= this.slides.length) {
      if (!this.loop) return;
      next = 0;
    }
    this._activate(next);
  }

  /** @param {1 | -1} direction */
  _step(direction) {
    const total = this.slides.length;
    this._activate((this.currentIndex + direction + total) % total);
  }

  goTo(index) {
    this._activate(index);
  }

  _onArrowClick(e) {
    this._step(e.currentTarget.dataset.direction === 'prev' ? -1 : 1);
  }

  _onSwipeStart(e) {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    this.swipeStart = { x: e.clientX, y: e.clientY, id: e.pointerId };
    window.addEventListener('pointerup', this._onSwipeEnd);
    window.addEventListener('pointercancel', this._onSwipeCancel);
  }

  _onSwipeEnd(e) {
    const start = this.swipeStart;
    this._onSwipeCancel();
    if (!start || e.pointerId !== start.id) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    if (Math.abs(dx) < HeroCarousel.SWIPE_MIN_PX || Math.abs(dx) < Math.abs(dy) * 1.2) return;
    this.swipedAt = performance.now();
    this._step(dx < 0 ? 1 : -1);
  }

  _onSwipeCancel() {
    this.swipeStart = null;
    window.removeEventListener('pointerup', this._onSwipeEnd);
    window.removeEventListener('pointercancel', this._onSwipeCancel);
  }

  _onTrackClick(e) {
    if (performance.now() - this.swipedAt > HeroCarousel.SWIPE_CLICK_GUARD_MS) return;
    e.preventDefault();
    e.stopPropagation();
  }

  pause() {
    if (this.isPaused) return;
    this.isPaused = true;
    this.setAttribute('data-paused', 'true');
    this._clearTimer();
  }

  resume() {
    if (!this.isPaused) return;
    this.isPaused = false;
    this.removeAttribute('data-paused');
    if (this.autoplay && this.isInViewport) {
      this._startAutoplay();
    }
  }

  _onSegmentClick(e) {
    const index = parseInt(e.currentTarget.dataset.index, 10);
    if (Number.isNaN(index)) return;
    this._activate(index);
  }

  _onSegmentKeydown(e) {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      const prev = this.currentIndex - 1 < 0 ? this.slides.length - 1 : this.currentIndex - 1;
      this._activate(prev);
      this.segments[prev]?.focus();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      const next = (this.currentIndex + 1) % this.slides.length;
      this._activate(next);
      this.segments[next]?.focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      this._activate(0);
      this.segments[0]?.focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      const last = this.slides.length - 1;
      this._activate(last);
      this.segments[last]?.focus();
    }
  }

  _onPointerEnter() { this.pause(); }
  _onPointerLeave() { this.resume(); }
  _onFocusIn() { if (this.pauseOnHover) this.pause(); }
  _onFocusOut(e) {
    if (this.contains(e.relatedTarget)) return;
    this.resume();
  }

  _onVisibilityChange() {
    if (document.hidden) {
      this.pause();
    } else if (this.isInViewport) {
      this.resume();
    }
  }

  _onIntersect(entries) {
    for (const entry of entries) {
      this.isInViewport = entry.isIntersecting;
      if (entry.isIntersecting) {
        if (!this.isPaused && this.autoplay && this.timeoutId === null) {
          this._startAutoplay();
        }
      } else {
        this._clearTimer();
      }
    }
  }
}

if (!customElements.get('hero-carousel')) {
  customElements.define('hero-carousel', HeroCarousel);
}
